import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ApiRouteConstants } from '../../../../../core/constants/api-routes';
import { FrnFilingTypeRequest } from '../../frn-filing-type/models/frn-filing-type-request';
import { FrnFilingTypeResponse } from '../../frn-filing-type/models/frn-filing-type-response';
import { FrnFilingCreationResponse } from '../models/frn-filing-creation-response';
import { FrnFilingCreationRequest } from '../models/frn-filing-creation-request';
import { StepValidationResult } from '../../../shared/models/step-validation-result';
import { StepNavigationService } from '../../../shared/services/step-navigation/step-navigation.service';
import { FrnFilingDetailRequest } from '../../frn-filing-detail/models/frn-filing-detail-request';
import { FrnFilingDetailResponse } from '../../frn-filing-detail/models/frn-filing-detail-response';
import { FrnFilingSubmitToEwaResponse } from '../models/frn-filing-submit-to-ewa-response';
import { FrnFilingSubmitToEwaRequest } from '../models/frn-filing-submit-to-ewa-request';
import { FrnFilingSyncToDirectEntryResponse } from '../models/frn-filing-sync-to-direct-entry-response';

@Injectable({ providedIn: 'root' })
export class FrnFilingService {
    private readonly frnFilingApi = ApiRouteConstants.FrnFiling;

    constructor(
        private readonly http: HttpClient,
        private readonly stepNavigationService: StepNavigationService,
    ) {}

    /**
     * Creates a new FRN Filing (FORM-160).
     *
     * @param request The payload needed to start an FRN filing.
     * @returns Observable that emits the created FRN filing response.
     */
    createFrnFiling(
        request: FrnFilingCreationRequest,
    ): Observable<FrnFilingCreationResponse> {
        return this.http.post<FrnFilingCreationResponse>(
            this.frnFilingApi.rootUrl,
            request,
        );
    }

    /**
     * Retrieves the data needed to populate FRN Filing Type (Page 1).
     *
     * @param applicationId The application ID.
     * @returns Observable that emits the FRN filing type response.
     */
    getFrnFilingType(applicationId: string): Observable<FrnFilingTypeResponse> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'type',
            applicationId,
        );

        return this.http.get<FrnFilingTypeResponse>(url);
    }

    /**
     * Updates the Page 1 FRN Filing Type selection.
     *
     * @param applicationId The application ID.
     * @param request The updated filing type request payload.
     * @returns Observable that emits the step validation result for FRN Type.
     */
    updateFrnFilingType(
        applicationId: string,
        request: FrnFilingTypeRequest,
    ): Observable<StepValidationResult> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'type',
            applicationId,
        );

        return this.http.post<StepValidationResult>(url, request);
    }

    /**
     * Retrieves the data needed to populate FRN Filing Detail (Page 2).
     *
     * @param applicationId The application ID.
     * @returns Observable that emits the FRN filing detail response.
     */
    getFrnFilingDetail(
        applicationId: string,
    ): Observable<FrnFilingDetailResponse> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'detail',
            applicationId,
        );

        return this.http.get<FrnFilingDetailResponse>(url);
    }

    /**
     * Updates the Page 2 FRN Filing Detail selection.
     *
     * @param applicationId The application ID.
     * @param request The updated filing detail request payload.
     * @returns Observable that emits the step validation result for FRN Detail.
     */
    updateFrnFilingDetail(
        applicationId: string,
        request: FrnFilingDetailRequest,
    ): Observable<StepValidationResult> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'detail',
            applicationId,
        );

        return this.http.post<StepValidationResult>(url, request);
    }

    /**
     * Submits the FRN filing to EWA.
     *
     * @param applicationId The application ID.
     * @param request The request object for submitting to EWA.
     * @returns FrnFilingSubmitToEwaResponse indicating submission status.
     */
    submitToEwa(
        applicationId: string,
        request: FrnFilingSubmitToEwaRequest,
    ): Observable<FrnFilingSubmitToEwaResponse> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'submitToEwa',
            applicationId,
        );

        return this.http.post<FrnFilingSubmitToEwaResponse>(url, request);
    }

    /**
     * Retrieves validation results for steps that have already been previously validated
     *
     * @param applicationId The application ID associated with the FRN filing.
     * @returns Observable that emits an array of step validation results for
     * previously saved steps only.
     */
    getPreviouslySavedStepValidations(
        applicationId: string,
    ): Observable<StepValidationResult[]> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'validatePreviousSteps',
            applicationId,
        );

        return this.http.get<StepValidationResult[]>(url);
    }

    /**
     * Re-validates all FRN Filing steps for the given application.
     *
     * @param applicationId The application ID associated with the FRN filing.
     * @returns Observable emitting the validation results for all steps.
     */
    validateAllSteps(
        applicationId: string,
    ): Observable<StepValidationResult[]> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'validateAllSteps',
            applicationId,
        );

        return this.http.get<StepValidationResult[]>(url);
    }

    /**
     * Initializes FRN filing step navigation and applies server side validation
     * results to either all steps or only previously saved steps.
     *
     * @param applicationId The application ID associated with the frn filing.
     * @param navigateToFirstInvalid Whether to automatically navigate to the first
     * invalid step after validations are applied.
     * @param validateAllSteps Whether to validate all frn filing steps or only
     * previously saved steps.
     * @returns Observable that completes once step initialization and validation
     * state updates have been applied.
     */
    getAndSetFrnStepsWithValidations(
        applicationId: string,
        navigateToFirstInvalid = true,
        validateAllSteps = false,
    ): Observable<void> {
        this.stepNavigationService.initializeFrnFilingSteps(applicationId);

        const validations$ = validateAllSteps
            ? this.validateAllSteps(applicationId)
            : this.getPreviouslySavedStepValidations(applicationId);

        return validations$.pipe(
            tap((validations: StepValidationResult[]) => {
                this.stepNavigationService.setStepValidations(
                    validations,
                    navigateToFirstInvalid,
                );
            }),
            map(() => void 0),
        );
    }

    /**
     * Syncs FRN Filing data to the associated Direct Entry application.
     *
     * @param applicationId The application ID of the FRN Filing.
     * @returns Observable that emits the sync response result.
     */
    syncToDirectEntry(
        applicationId: string,
    ): Observable<FrnFilingSyncToDirectEntryResponse> {
        const url = this.frnFilingApi.getFullRouteWithApplicationId(
            'syncToDirectEntry',
            applicationId,
        );

        return this.http.post<FrnFilingSyncToDirectEntryResponse>(url, {});
    }
}
